import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { AuthContext } from "../contexts/AuthContext";
import { PageLoadingAnimation } from "../components/loading/pageLoading";
import { SetupLoadingAnimation } from "../components/loading/setupLoading";
import { SetupFailed } from "../components/loading/setupFailed";

const maxRetries = 10;

const ProtectedRoute = ({
  children,
  requiredRole = false,
  allowInitialization = false,
  requireSuperadmin = false,
}) => {
  const { isAuthenticated, isLoading } = useKindeAuth();
  const { authContext, authContextLoading, retryCount, provisioningFailed } =
    useContext(AuthContext);
  const navigate = useNavigate();

  const isLoadingState = isLoading || authContextLoading;

  const missingOrgSetup =
    !authContext.organization.orgId ||
    !authContext.organization.stripeCustomerId;

  // 🔄 Navigate if all loading is done
  useEffect(() => {
    if (isLoadingState) return;

    if (!isAuthenticated) {
      navigate("/auth/login/");
      return;
    }

    if (!authContext.organization.orgId) {
      return; // still waiting on webhook
    }

    if (
      !authContext.organization.initializationComplete &&
      !allowInitialization
    ) {
      navigate("/settings/organization/");
      return;
    }

    if (requireSuperadmin) {
      const allowedIds = (
        process.env.REACT_APP_SUPERADMIN_KINDE_IDS || ""
      )
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
      if (!allowedIds.includes(authContext.user.authId)) {
        navigate("/auth/unauthorized/");
        return;
      }
    }

    if (requiredRole) {
      // Skip the role check if roles haven't loaded into the JWT yet. This
      // covers two races: (1) pre-init, where the org-creator's role webhook
      // hasn't fired yet; (2) post-init, where init just completed and the
      // webhook fired but the current JWT was issued before the role was
      // assigned. In both cases roles === [] transiently; invited users always
      // have their role in the JWT from first login, so this is safe.
      const isNewAccount =
        !authContext.organization.initializationComplete ||
        authContext.user.roles.length === 0;
      if (!isNewAccount) {
        const hasRequiredRole = authContext.user.roles.some(
          (role) => role.key === requiredRole
        );
        if (!hasRequiredRole) {
          navigate("/auth/unauthorized/");
        }
      }
    }
  }, [
    isLoadingState,
    isAuthenticated,
    authContext.organization.orgId,
    authContext.organization.initializationComplete,
    allowInitialization,
    requireSuperadmin,
    requiredRole,
    authContext.user.roles,
    authContext.user.authId,
    navigate,
  ]);

  if (provisioningFailed) {
    return <SetupFailed />;
  }

  // 🌀 Show loading UI
  if (isLoadingState) {
    if (retryCount > 0) {
      if (retryCount >= maxRetries) {
        return <SetupFailed />;
      }
      return <SetupLoadingAnimation />;
    }

    return (
      <PageLoadingAnimation
        isLoading={true}
        navSpacer={true}
        nonAnimated={true}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
